import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Events.css";
import { fetchEvents, getGalleryUrlsForEvent } from "../services/eventsApi";

function cardExcerpt(ev) {
  const ex = String(ev.excerpt || "").trim();
  if (ex) return ex;
  const c = String(ev.content || "").replace(/\s+/g, " ").trim();
  if (!c) return "";
  return c.length > 160 ? `${c.slice(0, 160).trim()}…` : c;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await fetchEvents();
        if (cancelled) return;
        setItems(data || []);
      } catch (e) {
        if (cancelled) return;
        setError("Failed to load events.");
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = useMemo(() => items.find((x) => x.featured) || items[0], [items]);
  const rest = useMemo(() => items.filter((x) => x !== featured), [items, featured]);
  const featuredGalleryCount = useMemo(
    () => (featured ? getGalleryUrlsForEvent(featured).length : 0),
    [featured]
  );

  const isEmpty = !loading && !error && items.length === 0;

  return (
    <div className="eventsPage">
      <div className="eventsWrap">
        <h1 className="eventsTitle">Events</h1>

        {error ? (
          <div className="eventsError">
            <div className="eventsErrorTitle">Something went wrong</div>
            <div className="eventsErrorMsg">{error}</div>
            <button className="eventsBtn" type="button" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : null}

        {isEmpty ? (
          <div className="eventsEmpty" role="status">
            <div className="eventsEmptyTitle">No Events yet</div>
          </div>
        ) : null}

        {loading ? (
          <div className="eventsFeatured eventsFeaturedLoading" aria-hidden="true">
            <div className="eventsSkelImg" />
            <div className="eventsSkelRight">
              <div className="eventsSkelLine w40" />
              <div className="eventsSkelLine w90" />
              <div className="eventsSkelLine w85" />
              <div className="eventsSkelLine w80" />
              <div className="eventsSkelLine w55" style={{ marginTop: 14 }} />
            </div>
          </div>
        ) : featured ? (
          <Link className="eventsFeaturedLink" to={`/Events/${featured.id}`}>
            <div className="eventsFeatured">
              <div className="eventsFeaturedImg">
                <img src={featured.imageUrl} alt={featured.title} />
              </div>
              <div className="eventsFeaturedBody">
                <div className="eventsMeta">
                  <span className="eventsMetaStrong">
                    {featured.category}, {featured.tag}
                  </span>
                  <span className="eventsDot">—</span>
                  <span>{formatDate(featured.date)}</span>
                </div>
                <h2 className="eventsFeaturedHeadline">{featured.title}</h2>
                <p className="eventsExcerpt">{cardExcerpt(featured)}</p>
                {featuredGalleryCount > 0 ? (
                  <p className="eventsCardGalleryHint">{featuredGalleryCount} photos in gallery</p>
                ) : null}
                <div className="eventsAuthor">
                  <div className="eventsAvatar" aria-hidden="true">
                    {initials(featured.author?.name)}
                  </div>
                  <div>
                    <div className="eventsAuthorName">{featured.author?.name}</div>
                    <div className="eventsAuthorRole">{featured.author?.role}</div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ) : null}

        {!isEmpty ? (
          <div className="eventsPager" aria-hidden="true">
            <span className="eventsPagerDot active" />
            <span className="eventsPagerDot" />
            <span className="eventsPagerDot" />
            <span className="eventsPagerDot" />
          </div>
        ) : null}

        {!isEmpty ? (
          <div className="eventsGrid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="eventsCard eventsCardLoading" aria-hidden="true">
                    <div className="eventsSkelCardImg" />
                    <div className="eventsCardBody">
                      <div className="eventsSkelLine w35" />
                      <div className="eventsSkelLine w95" />
                      <div className="eventsSkelLine w90" />
                      <div className="eventsSkelLine w70" />
                      <div className="eventsSkelLine w55" style={{ marginTop: 10 }} />
                    </div>
                  </div>
                ))
              : rest.map((ev) => {
                  const galleryCount = getGalleryUrlsForEvent(ev).length;
                  return (
                    <Link key={ev.id} className="eventsCardLink" to={`/Events/${ev.id}`}>
                      <article className="eventsCard">
                        <div className="eventsCardImg">
                          <img src={ev.imageUrl} alt={ev.title} />
                        </div>
                        <div className="eventsCardBody">
                          <div className="eventsMeta small">
                            <span className="eventsMetaStrong">
                              {ev.category}, {ev.tag}
                            </span>
                            <span className="eventsDot">—</span>
                            <span>{formatDate(ev.date)}</span>
                          </div>
                          <div className="eventsCardTitle">{ev.title}</div>
                          <p className="eventsCardExcerpt">{cardExcerpt(ev)}</p>
                          {galleryCount > 0 ? (
                            <p className="eventsCardGalleryHint">{galleryCount} photos in gallery</p>
                          ) : null}
                          <div className="eventsAuthor small">
                            <div className="eventsAvatar" aria-hidden="true">
                              {initials(ev.author?.name)}
                            </div>
                            <div>
                              <div className="eventsAuthorName">{ev.author?.name}</div>
                              <div className="eventsAuthorRole">{ev.author?.role}</div>
                            </div>
                          </div>
                        </div>
                      </article>
                    </Link>
                  );
                })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

